import Dexie, { type Table } from 'dexie';

import { type InvoiceApiPayload, type InvoiceDraft, createIdempotencyKey } from '~/schemas/invoice';

/**
 * Persistência local do app:
 *
 *  - `drafts`            : rascunho do wizard por empresa (clientId é a chave).
 *  - `pending_invoices`  : emissões que falharam por rede e aguardam drain
 *                          quando o navegador voltar a ficar online.
 *
 * Sprint 4 introduziu `drafts`. Sprint 7 adiciona `pending_invoices` na v2 —
 * Dexie aplica migration automática só adicionando a tabela nova. A v3 acrescenta
 * `idempotencyKey` às duas tabelas e preenche o que já estava gravado. A v4 dá
 * estado terminal (`failedAt`) às emissões que esgotam as tentativas.
 */

export interface PendingInvoice {
  id?: number;
  clientId: number;
  payload: InvoiceApiPayload;
  createdAt: number;
  /** Última tentativa de envio que falhou — null se nunca tentou drenar. */
  lastError: string | null;
  /** Número de tentativas; usamos pra parar de drenar em loop. */
  attempts: number;
  /**
   * A mesma chave usada na tentativa que falhou. É o que faz o drain reenviar
   * sem risco de emitir a nota duas vezes.
   */
  idempotencyKey: string;
  /**
   * Momento em que o drain desistiu após esgotar as tentativas. `null` enquanto
   * ainda está na fila de retry. Quando preenchido, o item vira uma falha terminal:
   * não é mais reenviado automaticamente nem contado como "pendente" — espera o
   * usuário mandar tentar de novo ou descartar. Evita ficar pendente pra sempre.
   */
  failedAt: number | null;
}

class RxDb extends Dexie {
  drafts!: Table<InvoiceDraft, number>;
  pending_invoices!: Table<PendingInvoice, number>;

  constructor() {
    super('rx-app-customer');
    this.version(1).stores({
      drafts: 'clientId, updatedAt',
    });
    this.version(2).stores({
      drafts: 'clientId, updatedAt',
      pending_invoices: '++id, clientId, createdAt',
    });
    // v3 só acrescenta campo não indexado; o upgrade existe para backfill. Sem ele,
    // uma nota já enfileirada subiria sem chave e voltaria a ter risco de duplicar.
    this.version(3)
      .stores({
        drafts: 'clientId, updatedAt',
        pending_invoices: '++id, clientId, createdAt',
      })
      .upgrade(async (transaction) => {
        await transaction
          .table<PendingInvoice>('pending_invoices')
          .toCollection()
          .modify((pending) => {
            if (!pending.idempotencyKey) pending.idempotencyKey = createIdempotencyKey();
          });
        await transaction
          .table<InvoiceDraft>('drafts')
          .toCollection()
          .modify((draft) => {
            if (!draft.idempotencyKey) draft.idempotencyKey = createIdempotencyKey();
          });
      });
    // v4 introduz `failedAt` e, na subida, zera o estado de retry de tudo que estava
    // na fila. Itens estagnados por uma falha sistêmica já corrigida (ex.: o CORS que
    // bloqueava o header de idempotência) voltam a tentar uma vez. A chave de
    // idempotência garante que reenviar não duplica caso algum tenha chegado ao servidor.
    this.version(4)
      .stores({
        drafts: 'clientId, updatedAt',
        pending_invoices: '++id, clientId, createdAt',
      })
      .upgrade(async (transaction) => {
        await transaction
          .table<PendingInvoice>('pending_invoices')
          .toCollection()
          .modify((pending) => {
            pending.attempts = 0;
            pending.lastError = null;
            pending.failedAt = null;
          });
      });
  }
}

export const draftDb = new RxDb();

export async function loadDraft(clientId: number): Promise<InvoiceDraft | undefined> {
  return draftDb.drafts.get(clientId);
}

export async function saveDraft(draft: InvoiceDraft): Promise<void> {
  await draftDb.drafts.put({ ...draft, updatedAt: Date.now() });
}

export async function clearDraft(clientId: number): Promise<void> {
  await draftDb.drafts.delete(clientId);
}

// --- Fila offline de emissões ----------------------------------------------

const MAX_DRAIN_ATTEMPTS = 5;

const isRetryable = (item: PendingInvoice): boolean => item.failedAt == null;
const hasFailed = (item: PendingInvoice): boolean => item.failedAt != null;

export async function enqueuePendingInvoice(
  clientId: number,
  payload: InvoiceApiPayload,
  idempotencyKey: string,
): Promise<number> {
  return draftDb.pending_invoices.add({
    clientId,
    payload,
    createdAt: Date.now(),
    lastError: null,
    attempts: 0,
    idempotencyKey,
    failedAt: null,
  });
}

/** Itens que o drain ainda deve tentar reenviar (não esgotaram as tentativas). */
export async function listRetryableInvoices(): Promise<PendingInvoice[]> {
  const items = await draftDb.pending_invoices.orderBy('createdAt').toArray();
  return items.filter(isRetryable);
}

/** Contagem de pendentes em voo — alimenta o banner. Falhas terminais não entram. */
export async function countPendingInvoices(): Promise<number> {
  return draftDb.pending_invoices.filter(isRetryable).count();
}

/** Emissões que o drain abandonou; esperam ação do usuário (tentar de novo / descartar). */
export async function countFailedInvoices(): Promise<number> {
  return draftDb.pending_invoices.filter(hasFailed).count();
}

export async function removePendingInvoice(id: number): Promise<void> {
  await draftDb.pending_invoices.delete(id);
}

/**
 * Registra a falha de uma tentativa. Ao atingir `MAX_DRAIN_ATTEMPTS`, marca `failedAt`
 * e o item vira falha terminal — sai da fila de retry em vez de continuar drenando em
 * loop ou de ficar pendente indefinidamente.
 */
export async function markPendingInvoiceFailure(id: number, error: string): Promise<void> {
  const attempts = ((await draftDb.pending_invoices.get(id))?.attempts ?? 0) + 1;
  await draftDb.pending_invoices.update(id, {
    lastError: error,
    attempts,
    failedAt: attempts >= MAX_DRAIN_ATTEMPTS ? Date.now() : null,
  });
}

/** Recoloca todas as falhas terminais na fila de retry (ação manual do usuário). */
export async function retryFailedInvoices(): Promise<void> {
  await draftDb.pending_invoices
    .filter(hasFailed)
    .modify({ attempts: 0, lastError: null, failedAt: null });
}

/** Descarta as falhas terminais — usuário optou por não reenviar. */
export async function discardFailedInvoices(): Promise<void> {
  await draftDb.pending_invoices.filter(hasFailed).delete();
}

/** Esvazia rascunhos e fila offline. Usado no logout e na troca de usuário. */
export async function clearOfflineData(): Promise<void> {
  await draftDb.transaction('rw', draftDb.drafts, draftDb.pending_invoices, async () => {
    await draftDb.drafts.clear();
    await draftDb.pending_invoices.clear();
  });
}

export { MAX_DRAIN_ATTEMPTS };
